import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Simple form select dropdown.
 * Props:
 *  - options: string[]  e.g. ["1 – 10", "11 – 50"]
 *  - value: string       currently selected option
 *  - onChange: (value: string) => void
 *  - placeholder: string
 *  - error: boolean       shows red border when true
 */
export default function FormDropdown({ options = [], value, onChange, placeholder = "Select...", error = false }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (opt) => {
    onChange(opt);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-3 py-2.5 sm:py-2 rounded-lg text-[15px] sm:text-sm font-sans border transition-all outline-none bg-white text-left ${
          error ? "border-red-500" : "border-black/20 focus:border-[#1a1a1a]"
        }`}
      >
        <span className={value ? "text-[#1a1a1a]" : "text-gray-500"}>
          {value || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-black/10 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => handleSelect(opt)}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${
                value === opt ? "bg-gray-100 font-medium" : ""
              }`}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}